import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

/** Кнопка-иконка 48 × 48 — единственный способ поставить действие в шапку. */
export function IconButton({
  icon,
  label,
  onClick,
  filled = false,
  className,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  filled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`icon-button ${className ?? ''}`}
      aria-label={label}
      onClick={onClick}
    >
      <Icon name={icon} filled={filled} size={24} />
    </button>
  );
}

/**
 * Шапка **внутреннего** экрана: «Назад», заголовок, действия справа.
 *
 * Корневые разделы («Дела», «Задачи») ставят `root` — у них нет «Назад», а заголовок
 * набран крупнее: по величине заголовка видно, находишься ты в разделе или внутри
 * записи. Это не расхождение, а разница уровней — docs/DESIGN.md, «Переиспользование».
 *
 * Стрелка «Назад» зеркалится вместе с направлением письма: раскладка везде идёт
 * через `start`/`end`, а не `left`/`right`.
 *
 * `contextual` — панель мультивыбора: вместо «Назад» стоит «Снять выделение», фон
 * другой, и по нему сразу видно, что экран в особом состоянии. Отдельной шапкой
 * это не делается: кнопки, высота и тон иконок обязаны остаться общими
 * (docs/DESIGN.md, «Переиспользование»).
 */
export function AppTopBar({
  title,
  subtitle,
  onBack,
  leading,
  actions,
  root = false,
  contextual = false,
}: {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  /** Своя кнопка слева вместо «Назад» — например «Снять выделение». */
  leading?: ReactNode;
  actions?: ReactNode;
  root?: boolean;
  contextual?: boolean;
}) {
  const withLeading = Boolean(onBack) || Boolean(leading);

  return (
    <header
      className={
        `top-bar ${root ? 'top-bar--root' : ''} ` +
        `${withLeading ? 'top-bar--with-back' : ''} ` +
        `${contextual ? 'top-bar--contextual' : ''}`
      }
    >
      {leading ?? (onBack ? <IconButton icon="arrow_back" label="Назад" onClick={onBack} /> : null)}
      <div className="top-bar__texts">
        <h1 className="top-bar__title">{title}</h1>
        {subtitle ? <p className="top-bar__subtitle">{subtitle}</p> : null}
      </div>
      {actions}
    </header>
  );
}
